import {
  globalObservability,
  initObservability as init, // eslint-disable-next-line no-restricted-syntax
  useObservability as useObservabilityLib, // eslint-disable-next-line no-restricted-syntax
} from '@opendoor/observability/slim';
import type { ErrorEvent } from '@sentry/nextjs';

import {
  DATADOG_RUM_APPLICATION_CLIENT_TOKEN,
  DATADOG_RUM_APPLICATION_ID,
  DEPLOY_ENV,
} from '../components/globals';

export type CosmosTrackingCategory = 'cosmos';

export type CosmosTrackingActions =
  | 'inputting_address'
  | 'inputting_address_success'
  | 'property-view'
  | 'property-favorite'
  | 'property-share'
  | 'property-similar-listings'
  | 'property-purchase-calculator'
  | 'property-images'
  | 'carousel_slide_view';

export type GlobalObservabilityContext = Array<{ key: string; value: string }>;
interface ObservabilityOptions {
  globalContext?: GlobalObservabilityContext;
  isBot?: boolean;
}

const EXPERIMENT_QUERY_PARAM_KEYS = [
  /* Experiment name */
  'experiment',
  /* Experiment variation */
  'experimentVariation',
];

export const OBSERVABILITY_QUERY_PARAM_KEYS = [
  ...EXPERIMENT_QUERY_PARAM_KEYS,
  /* Cosmos path e.g. /landing-pages/[...slug] or /articles/[...name] */
  'cosmosPathname',
  /* Dynamic route value e.g. $slug in /landing-pages/[...slug] */
  'slug',
  /* Dynamic route value e.g. $name in /articles/topic/[...name] */
  'name',
];

// Browser-fixed messages for a fetch() that never got a response — navigation
// away mid-flight, network drop, or blocker. One string per engine.
const NETWORK_ABORT_MESSAGES = [
  // WebKit (Safari and every iOS browser)
  'Load failed',
  // Blink (Chrome, Edge)
  'Failed to fetch',
  // Gecko (Firefox)
  'NetworkError when attempting to fetch a resource.',
];

// Public first path segments served by cosmos: pages/ routes plus
// worker-rewritten paths whose public segment differs (/checkout →
// buy-direct-checkout). The segment is URL-controlled — junk URLs render the
// 404 shell — so unknown segments collapse into 'other' to bound Sentry issue
// cardinality.
const KNOWN_ROUTE_SECTIONS = new Set([
  'account',
  'address-entry-mobile',
  'buy-direct-checkout',
  'checkout',
  'dashboard',
  'error',
  'favorites',
  'feed',
  'homes',
  'hub',
  'key-agent-sign-up',
  'key-download',
  'login',
  'mail',
  'manual-address-entry',
  'marketplace',
  'mobile-login',
  'nl-search',
  'properties',
  'reset-password',
  'reset-password-edit',
  'self-tour',
  'token',
  'verify',
]);

/**
 * Per-route-section Sentry fingerprint for aborted-fetch TypeErrors, or
 * undefined for every other event. These errors carry no stacktrace, so under
 * default grouping every page's aborts collapse into a single app-wide issue
 * where a real per-page regression is invisible.
 */
export const networkAbortFingerprint = (
  event: Pick<ErrorEvent, 'exception'>,
  pathname: string,
): string[] | undefined => {
  const exception = event.exception?.values?.[0];
  if (exception?.type !== 'TypeError') {
    return undefined;
  }
  if (!NETWORK_ABORT_MESSAGES.includes(exception.value ?? '')) {
    return undefined;
  }
  const routeSection = pathname.split('/')[1];
  if (!routeSection) {
    return ['network-abort', 'root'];
  }
  return ['network-abort', KNOWN_ROUTE_SECTIONS.has(routeSection) ? routeSection : 'other'];
};

// Unloading the document terminates its fetch group, rejecting every in-flight
// request with one of NETWORK_ABORT_MESSAGES. Those aborts are the browser
// cancelling work the page no longer needs, so they are dropped rather than
// fingerprinted — a genuine abort on a page that is staying put still reports.
let navigatingAway = false;

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    navigatingAway = true;
  });
  // pagehide also fires on the way into the bfcache; a restored page is live
  // again and its aborts are real.
  window.addEventListener('pageshow', () => {
    navigatingAway = false;
  });
}

/**
 * Marks a deliberate hard navigation. `pagehide` covers most unloads, but a
 * caller that is about to `location.assign` can say so up front rather than
 * depend on the unload event landing before the cancelled fetch rejects.
 */
export const markNavigatingAway = (): void => {
  navigatingAway = true;
};

/** Exported for tests; the flag is otherwise module lifetime. */
export const __resetNavigatingAwayForTest = () => {
  navigatingAway = false;
};

//observability
export const initObservability = ({ globalContext, isBot }: ObservabilityOptions = {}) => {
  // make sure we only init observability client-side

  if (typeof window !== 'undefined' && !globalObservability.isInitialized()) {
    if (DEPLOY_ENV === 'test') {
      init<CosmosTrackingCategory, CosmosTrackingActions>({
        eventTrackingCategory: 'cosmos',
        isBot: true,
        env: 'development',
        appName: 'cosmos',
        version: process.env.NEXT_PUBLIC_VERSION || 'unknown',
      });
      return;
    }

    init<CosmosTrackingCategory, CosmosTrackingActions>({
      eventTrackingCategory: 'cosmos',
      isBot,
      env: DEPLOY_ENV === 'localdev' ? 'development' : DEPLOY_ENV,
      appName: 'cosmos',
      version: process.env.NEXT_PUBLIC_VERSION || 'unknown',
      lazyLoadAnalytics: true,
      sentry: {
        dsn: 'https://3414e8de867a4131a44598d29006eccc@o117144.ingest.sentry.io/1439728',
        maxBreadcrumbs: 50,
        attachStacktrace: true,
        // Only allow errors from first-party sources, to reduce sentry spam
        allowUrls: ['simplersell.com', 'opendoor.com'],
        // observability package has default ignore errors set by default
        // https://github.com/opendoor-labs/code/blob/master/js/packages/observability/src/internal/sentry.ts#L138
        init: async (initOpts) => {
          const customBeforeSend = (event: ErrorEvent, hint: any) => {
            // ref: https://github.com/getsentry/sentry-javascript/issues/7941#issuecomment-1525640063

            // {
            //   "type": "Event",
            //   "value": "Non-Error exception captured with keys: currentTarget, isTrusted, target, type",
            //   "stacktrace": {
            //     "frames": [
            //       {
            //         "module": "properties/21027-Manon-Ln-Spring-TX-77388/aid_1acb88dd-b5de-5b5f-89c8-66bf60db11fd",
            //         "filename": "app:///properties/21027-Manon-Ln-Spring-TX-77388/aid_1acb88dd-b5de-5b5f-89c8-66bf60db11fd",
            //         "abs_path": "app:///properties/21027-Manon-Ln-Spring-TX-77388/aid_1acb88dd-b5de-5b5f-89c8-66bf60db11fd",
            //         "in_app": true
            //       }
            //     ]
            //   },
            //   "mechanism": {
            //     "type": "onerror",
            //     "synthetic": true,
            //     "handled": false
            //   }
            // }
            const eventMessage = event?.exception?.values?.[0]?.value || '';
            if (
              eventMessage.includes?.(
                'Non-Error exception captured with keys: currentTarget, isTrusted, target, type',
              )
            ) {
              return null;
            }
            if ((window?.navigator?.userAgent || '').includes('Netsparker')) {
              return null;
            }
            const fingerprint = networkAbortFingerprint(event, window.location.pathname);
            if (fingerprint && !navigatingAway) {
              event.fingerprint = fingerprint;
            } else if (fingerprint) {
              return null;
            }
            // Preserve upstream drops: the observability package's beforeSend
            // returns null to discard events, and `?? event` would resurrect
            // every one of them.
            return initOpts.beforeSend ? initOpts.beforeSend(event, hint) : event;
          };
          const Sentry = await import('@sentry/nextjs');
          Sentry.init({
            ...initOpts,
            beforeSend: customBeforeSend,
            tracesSampleRate: 0,
          });
          window.addEventListener('unhandledrejection', (event) => {
            const reason =
              event.reason ?? new Error('Unhandled promise rejection with undefined reason');
            Sentry.captureException(reason, {
              contexts: {
                promiseRejection: {
                  hadReason: event.reason != null,
                  promiseType: event.promise?.constructor?.name,
                },
              },
            });
          });
          return Sentry;
        },
      },
      datadog: {
        service: 'cosmos',
        applicationId: DATADOG_RUM_APPLICATION_ID,
        clientToken: DATADOG_RUM_APPLICATION_CLIENT_TOKEN,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 15,
        trackUserInteractions: true,
        silentMultipleInit: true,
        trackSessionAcrossSubdomains: true,
        // Please be careful when adding tracing origins.
        // If a server is not prepared for the new Datadog headers, then it may fail with CORS errors.
        // Relevant postmortem: https://docs.google.com/document/d/1O9eWUdXZJOS4Oha2v_dtPfjO34IU3KEDpNqjk3AtXdk/edit
        allowedTracingUrls: [/https:\/\/.*\.simplersell\.com/, /https:\/\/.*\.opendoor\.com/],
        defaultPrivacyLevel: 'mask-user-input',
        init: async (initOpts) => {
          const datadogRum = (await import('@datadog/browser-rum')).datadogRum;
          datadogRum.init(initOpts);
          datadogRum.startSessionReplayRecording();
          globalContext?.forEach(({ key, value }) => {
            datadogRum.setGlobalContextProperty(key, `${value}`);
          });
          return datadogRum;
        },
      },
      journeyEndpoint: '/_journey/api/v1',
    });
  }
};

export const useObservability = () =>
  useObservabilityLib<CosmosTrackingCategory, CosmosTrackingActions>();
